import { useState, useEffect, useCallback } from "react";

// Chakra breakpoint values in pixels (must match theme)
const BREAKPOINTS = {
  base: 0,
  sm: 480,
  md: 768,
  lg: 992,
  xl: 1280,
  "2xl": 1536,
};

/**
 * SSR-safe replacement for useBreakpointValue.
 * Returns defaultValue during SSR and initial render,
 * then calculates breakpoint on client using matchMedia.
 * 
 * @param {Object} values - Object with breakpoint keys and values, e.g. { base: "sm", lg: "xl" }
 * @param {*} defaultValue - Value to use during SSR (should match "base" value)
 */
export const useSSRBreakpointValue = (values, defaultValue) => {
  const [currentValue, setCurrentValue] = useState(defaultValue);

  const calculateValue = useCallback(() => {
    if (typeof window === "undefined") return defaultValue;

    const width = window.innerWidth;
    
    // Get breakpoint keys from values, sorted by breakpoint size (largest first)
    const breakpointKeys = Object.keys(values).sort((a, b) => {
      return (BREAKPOINTS[b] || 0) - (BREAKPOINTS[a] || 0);
    });

    // Find the first (largest) breakpoint that matches
    for (const key of breakpointKeys) {
      const breakpointWidth = BREAKPOINTS[key] || 0;
      if (width >= breakpointWidth) {
        return values[key];
      }
    }

    return defaultValue;
  }, [values, defaultValue]);

  useEffect(() => {
    // Calculate initial value on mount
    setCurrentValue(calculateValue());

    // Update on resize
    const handleResize = () => {
      setCurrentValue(calculateValue());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [calculateValue]);

  return currentValue;
};

export const useHeadingSize = () =>
  useSSRBreakpointValue({ base: "xl", lg: "2xl" }, "xl");
